import React from 'react';
import { useTranslation } from 'react-i18next';

// Types
import { RouteSegment } from '../../../../../types/surveillanceRoute';

// Components
import { SpecialTag } from '../../../../../components/display/SpecialTag';
import { TagActionsMenu } from '../../../../../components/display/SpecialTag/TagActionsMenu';
import { variant } from '../../../../../components/display/SpecialTag/types';
import { orderStateVariant } from '../../../../../components/display/SpecialTag/orderStateVariant';
import { LinkText, PendingCellText, CellStack, LinkWithState } from './styles';

interface AvisoOrderCellProps {
  segment: RouteSegment;
  /**
   * ¿Puede ligar? = `canLinkAvisoHere && !isSegmentPendingSync(segment) &&
   * canSegmentTakeLinks(segment)`. Lo calcula la tabla.
   */
  canLink: boolean;
  /** «Desvincular» de este tramo está viajando: la pill se pinta sin acciones. */
  busy: boolean;
  /** Abrir la OT. Solo la página lo pasa; los modales (sin router) no. */
  onOpenOrder?: () => void;
  onLink?: () => void;
  onEdit?: () => void;
  onUnlink?: () => void;
}

/**
 * Celda «OT sala» de la tabla de Tramos (spec 2026-10-27 § 12.2). La OT del
 * aviso se pinta como en la card de «Órdenes asociadas»: pill verde «Vinculado
 * con X» con su menú [Ver, Editar, Desvincular]. Sin hooks de router ni de
 * Redux: la tabla se pinta también dentro de modales fuera del router.
 * 30-oct: los trabajos del aviso ya no van aquí debajo (la fila crecía al
 * doble); van en la columna de la actuación.
 */
export const AvisoOrderCell = ({
  segment,
  canLink,
  busy,
  onOpenOrder,
  onLink,
  onEdit,
  onUnlink,
}: AvisoOrderCellProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common']);
  const isAviso = segment.pauseCause === 'external-call';
  const linked = segment.linkedExternalOrder;

  if (linked?.id) {
    const title = t('pages:surveillance.linkedWith', {
      roid: linked.roid ?? t('pages:surveillance.linkedExternalColumn'),
    });
    // Etiqueta del estado de la OT, junto a su píldora (misma fila).
    const stateVariant = orderStateVariant(linked.state);
    const stateTag = stateVariant ? (
      <SpecialTag
        variant={stateVariant}
        sizeTag="medium"
        data-testid="segment-row-aviso-state"
      />
    ) : null;
    // Mientras viaja el desvínculo, sin acciones: un segundo toque no hace nada.
    if (busy) {
      return (
        <CellStack>
          <LinkWithState>
            <SpecialTag
              variant={variant.LinkedFromSegment}
              sizeTag="medium"
              title={title}
              data-testid="segment-row-aviso-busy"
            />
            {stateTag}
          </LinkWithState>
        </CellStack>
      );
    }
    const actions = [
      ...(onOpenOrder
        ? [
            {
              icon: 'view' as const,
              label: t('common:buttons.view'),
              onClick: onOpenOrder,
            },
          ]
        : []),
      // Editar y Desvincular solo en el aviso: otra causa con OT es un dato
      // antiguo que se enseña pero no se toca (como el PDF).
      ...(canLink && isAviso && onEdit && onUnlink
        ? [
            {
              icon: 'edit' as const,
              label: t('common:buttons.edit'),
              onClick: onEdit,
            },
            {
              icon: 'unlink' as const,
              label: t('common:buttons.unlink'),
              onClick: onUnlink,
            },
          ]
        : []),
    ];
    return (
      <CellStack>
        <LinkWithState>
          {actions.length > 0 ? (
            <TagActionsMenu
              variant={variant.LinkedFromSegment}
              sizeTag="medium"
              title={title}
              testId="segment-row-aviso-actions"
              actions={actions}
            />
          ) : (
            <SpecialTag
              variant={variant.LinkedFromSegment}
              sizeTag="medium"
              title={title}
            />
          )}
          {stateTag}
        </LinkWithState>
      </CellStack>
    );
  }

  const showLink = canLink && isAviso && Boolean(onLink);
  // Quien puede ligar ve solo «Vincular aviso», que ya dice lo que falta
  // (decisión del usuario, 29-oct); el ámbar queda para quien no puede.
  const showPending = !showLink && Boolean(segment.pendingExternalLink);
  if (!showPending && !showLink) return <>-</>;
  return (
    <CellStack>
      {showPending && (
        /* Un aviso a sala sin OT se veía con «-», igual que un tramo que
           nunca tuvo aviso (Su.0004006, 17/10): lo dice con el texto de la
           card. */
        <PendingCellText>
          {t('pages:surveillance.pendingExternalLink')}
        </PendingCellText>
      )}
      {showLink && (
        <LinkText
          type="button"
          data-testid="segment-row-link-aviso"
          onClick={onLink}
        >
          {t('pages:surveillance.linkAviso')}
        </LinkText>
      )}
    </CellStack>
  );
};

export default AvisoOrderCell;
